import axios from 'axios';
import React, { useState, useRef } from 'react';
import { FileDown, Upload, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { API } from '../config';

/**
 * Reusable Excel template-download + upload panel.
 *
 * Props:
 *  - title:           string (panel heading)
 *  - description:     string (helper text under the title)
 *  - templateUrl:     API path (without /api prefix) to GET template, e.g. `/plants/${id}/rate-history/template.xlsx`
 *  - importUrl:       API path (without /api prefix) to POST import,   e.g. `/plants/${id}/rate-history/import`
 *  - templateFilename: filename for the downloaded template
 *  - token:           Bearer token (optional, cookie auth also works)
 *  - onImported:      callback fired after a successful import (refresh data)
 *  - testidPrefix:    used to build deterministic data-testid values
 *  - disabled:        bool — disable both buttons (e.g. when no plant selected)
 */
const ExcelImportPanel = ({
  title, description, templateUrl, importUrl, templateFilename,
  token, onImported, testidPrefix, disabled, allowOverwrite = false,
  exportUrl, exportFilename,
}) => {
  const fileInputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [overwrite, setOverwrite] = useState(false);
  const [result, setResult] = useState(null); // { status: 'success'|'error', message: string, details?: object }

  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

  const handleDownload = async () => {
    setResult(null);
    try {
      setBusy(true);
      const res = await axios.get(`${API}${templateUrl}`, {
        headers: authHeaders,
        responseType: 'blob',
      });
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = templateFilename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setResult({ status: 'success', message: `Template downloaded: ${templateFilename}` });
    } catch (err) {
      setResult({
        status: 'error',
        message: err.response?.data?.detail || err.message || 'Failed to download template',
      });
    } finally {
      setBusy(false);
    }
  };

  const handleFilePick = () => fileInputRef.current?.click();

  const handleExportDownload = async () => {
    if (!exportUrl) return;
    setResult(null);
    try {
      setBusy(true);
      const res = await axios.get(`${API}${exportUrl}`, { headers: authHeaders, responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = exportFilename || 'export.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setResult({ status: 'success', message: `Downloaded existing data: ${exportFilename || 'export.xlsx'}` });
    } catch (err) {
      setResult({ status: 'error', message: err.response?.data?.detail || err.message || 'Failed to download existing data' });
    } finally {
      setBusy(false);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;

    if (overwrite && !window.confirm(
      'OVERWRITE MODE: every row in the spreadsheet that matches an existing (date, shift) will REPLACE the current data. This cannot be undone. Continue?'
    )) return;

    setResult(null);
    try {
      setBusy(true);
      const fd = new FormData();
      fd.append('file', file);
      const url = `${API}${importUrl}${overwrite ? (importUrl.includes('?') ? '&' : '?') + 'overwrite=true' : ''}`;
      const res = await axios.post(url, fd, { headers: authHeaders });
      setResult({
        status: 'success',
        message: res.data.message,
        details: {
          inserted: res.data.inserted,
          updated: res.data.updated || 0,
          skipped: res.data.skipped_duplicates,
          errors: res.data.errors || [],
          total: res.data.total_rows,
          overwriteMode: res.data.overwrite_mode || false,
        },
      });
      if (onImported) await onImported();
    } catch (err) {
      setResult({
        status: 'error',
        message: err.response?.data?.detail || err.message || 'Upload failed',
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-4 sm:p-5 border-l-4 border-emerald-500" data-testid={`${testidPrefix}-panel`}>
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex-1 min-w-0">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 flex items-center">
            <FileDown className="h-5 w-5 mr-2 text-emerald-600 flex-shrink-0" />
            {title}
          </h3>
          <p className="text-xs sm:text-sm text-gray-600 mt-1">{description}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
          {allowOverwrite && (
            <label className={`flex items-center gap-2 text-xs font-medium px-3 py-2 rounded-lg border cursor-pointer select-none ${overwrite ? 'bg-amber-50 border-amber-400 text-amber-800' : 'bg-gray-50 border-gray-300 text-gray-700'}`} title="Admin only — replaces existing (date, shift) rows instead of skipping them">
              <input
                type="checkbox"
                checked={overwrite}
                onChange={(e) => setOverwrite(e.target.checked)}
                className="accent-amber-600"
                data-testid={`${testidPrefix}-overwrite-toggle`}
              />
              Overwrite existing
            </label>
          )}
          <button
            type="button"
            onClick={handleDownload}
            disabled={busy || disabled}
            className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-3 sm:px-4 py-2 min-h-[40px] rounded-lg disabled:opacity-50 flex items-center text-sm"
            data-testid={`${testidPrefix}-download-btn`}
          >
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileDown className="h-4 w-4 mr-2" />}
            Template
          </button>
          {exportUrl && (
            <button
              type="button"
              onClick={handleExportDownload}
              disabled={busy || disabled}
              className="bg-sky-600 hover:bg-sky-700 text-white font-semibold px-3 sm:px-4 py-2 min-h-[40px] rounded-lg disabled:opacity-50 flex items-center text-sm"
              data-testid={`${testidPrefix}-export-btn`}
              title="Download every existing row as an Excel file (round-trip editable)"
            >
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileDown className="h-4 w-4 mr-2" />}
              Existing Data
            </button>
          )}
          <button
            type="button"
            onClick={handleFilePick}
            disabled={busy || disabled}
            className="bg-[#156ACA] hover:bg-[#0f56a3] text-white font-semibold px-3 sm:px-4 py-2 min-h-[40px] rounded-lg disabled:opacity-50 flex items-center text-sm"
            data-testid={`${testidPrefix}-upload-btn`}
          >
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
            Import
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={handleFileChange}
            className="hidden"
            data-testid={`${testidPrefix}-file-input`}
          />
        </div>
      </div>

      {result && (
        <div
          className={`mt-3 px-3 py-2 rounded-lg text-sm ${
            result.status === 'success'
              ? 'bg-green-50 border border-green-200 text-green-800'
              : 'bg-red-50 border border-red-200 text-red-700'
          }`}
          data-testid={`${testidPrefix}-result`}
        >
          <div className="flex items-center">
            {result.status === 'success'
              ? <CheckCircle2 className="h-4 w-4 mr-2 flex-shrink-0" />
              : <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />}
            <span>{result.message}</span>
          </div>
          {result.details && (
            <div className="mt-2 text-xs text-gray-700 space-y-0.5">
              <div>
                Inserted: <strong>{result.details.inserted}</strong>
                {result.details.overwriteMode && <> · Updated: <strong className="text-amber-700">{result.details.updated}</strong></>}
                {' '}· Skipped duplicates: <strong>{result.details.skipped}</strong>
                {' '}· Total rows: <strong>{result.details.total}</strong>
              </div>
              {result.details.overwriteMode && (
                <div className="text-amber-700 font-semibold">⚠ Overwrite mode was ON — existing rows were replaced.</div>
              )}
              {result.details.errors.length > 0 && (
                <details className="mt-1">
                  <summary className="cursor-pointer font-semibold text-red-700">
                    {result.details.errors.length} row error(s)
                  </summary>
                  <ul className="mt-1 list-disc list-inside text-red-700">
                    {result.details.errors.slice(0, 20).map((er) => (
                      <li key={'row-' + er.row}>Row {er.row}: {er.error}</li>
                    ))}
                    {result.details.errors.length > 20 && <li key="more">...and {result.details.errors.length - 20} more</li>}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ExcelImportPanel;
